import { ErrorHandler, Injectable, inject, signal } from '@angular/core';
import { isChunkLoadFailure } from './chunk-load-recovery';

/**
 * Raised when the app discovers it is running against a deploy that has been
 * replaced. Read by the banner in AppComponent.
 */
@Injectable({ providedIn: 'root' })
export class NewVersionNotice {
  /** True once a lazy chunk has failed to load. Never goes back to false. */
  readonly stale = signal(false);

  markStale(): void {
    this.stale.set(true);
  }

  reload(): void {
    // location.reload() may serve the same cached index.html. A cache-busting
    // navigation gets the new one, which is the whole point.
    const url = new URL(window.location.href);
    url.searchParams.set('v', String(Date.now()));
    window.location.replace(url.toString());
  }
}

/**
 * Turns the browser's chunk-load failure into a sentence and an instruction.
 *
 * Without this the page half-renders: the user clicked a menu item, nothing
 * happened, and the only evidence is a console message about a MIME type. They
 * have no way to know a reload fixes it, and every subsequent click fails the
 * same way.
 *
 * Everything that is not a chunk failure is passed to Angular's own handler
 * untouched — offering "a new version shipped" for a null dereference would
 * send the reader chasing a deploy that never happened.
 */
@Injectable()
export class ChunkAwareErrorHandler implements ErrorHandler {
  private notice = inject(NewVersionNotice);
  private fallback = new ErrorHandler();

  handleError(error: unknown): void {
    if (isChunkLoadFailure(error)) {
      this.notice.markStale();
      return;
    }
    this.fallback.handleError(error);
  }
}
